import { useEffect, useMemo, useState } from 'react';

import type { AdminStore, StoreSettingsRow } from '@/client/types/stores';
import { useStoreSettingsQuery } from '@/client/api/stores/stores.query';
import { useUpdateStoreSettings } from '@/client/api/stores/stores.mutation';
import { Input } from '@/components/ui/input';

import { SectionCard, CardSkeleton, FormSkeleton } from '../shared';
import { ReasonDialog } from '../ReasonDialog';
import { FieldRow, SaveRow, Toggle } from './controls';

interface Form {
  order_alerts_enabled: boolean;
  low_stock_alerts_enabled: boolean;
  auto_refund_enabled: boolean;
  charge_vat: boolean;
  vat_rate: string;
  default_tax_category: string;
  default_shipping_rate: string;
  return_window_days: string;
  grace_period_days: string;
  /** Comma-separated levy tax ids (`default_taxids`). */
  default_taxids: string;
}

const taxidsToText = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : typeof v === 'string' ? v : '';

const textToTaxids = (v: string): number[] | null => {
  const ids = v.split(',').map((x) => x.trim()).filter(Boolean).map(Number);
  return ids.length ? ids : null;
};

const toForm = (s: StoreSettingsRow | null): Form => ({
  order_alerts_enabled: Boolean(s?.order_alerts_enabled),
  low_stock_alerts_enabled: Boolean(s?.low_stock_alerts_enabled),
  auto_refund_enabled: Boolean(s?.auto_refund_enabled),
  charge_vat: Boolean(s?.charge_vat),
  vat_rate: s?.vat_rate != null ? String(s.vat_rate) : '',
  default_tax_category: s?.default_tax_category ?? '',
  default_shipping_rate: s?.default_shipping_rate != null ? String(s.default_shipping_rate) : '',
  return_window_days: s?.return_window_days != null ? String(s.return_window_days) : '',
  grace_period_days: s?.grace_period_days != null ? String(s.grace_period_days) : '',
  default_taxids: taxidsToText(s?.default_taxids),
});

const numOrNull = (v: string): number | null => (v.trim() === '' ? null : Number(v));

export const OperationsSection: React.FC<{ store: AdminStore }> = ({ store }) => {
  const { data: settings, isLoading } = useStoreSettingsQuery(store.storeid);
  const update = useUpdateStoreSettings();

  const initial = useMemo(() => toForm(settings ?? null), [settings]);
  const [form, setForm] = useState<Form>(initial);
  useEffect(() => setForm(initial), [initial]);

  const [confirm, setConfirm] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const taxidsValid = textToTaxids(form.default_taxids)?.every((n) => Number.isInteger(n) && n > 0) ?? true;
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  if (isLoading)
    return (
      <div className="space-y-4">
        {[2, 4, 2, 2].map((fields, i) => <CardSkeleton key={i} description={i === 0}><FormSkeleton fields={fields} /></CardSkeleton>)}
      </div>
    );

  const save = (reason: string) => {
    update.mutate({
      storeid: store.storeid,
      reason,
      order_alerts_enabled: form.order_alerts_enabled ? 1 : 0,
      low_stock_alerts_enabled: form.low_stock_alerts_enabled ? 1 : 0,
      auto_refund_enabled: form.auto_refund_enabled ? 1 : 0,
      charge_vat: form.charge_vat ? 1 : 0,
      vat_rate: numOrNull(form.vat_rate),
      default_tax_category: form.default_tax_category.trim() || null,
      default_shipping_rate: numOrNull(form.default_shipping_rate),
      return_window_days: numOrNull(form.return_window_days),
      grace_period_days: numOrNull(form.grace_period_days),
      default_taxids: textToTaxids(form.default_taxids),
    }, { onSuccess: () => setConfirm(false) });
  };

  return (
    <div className="space-y-4">
      <SectionCard title="Notifications" description="Alert the seller about store activity.">
        <div className="divide-y divide-gray-100">
          <FieldRow label="Order notifications" hint="Notify when a new order comes in.">
            <Toggle checked={form.order_alerts_enabled} onChange={(v) => set('order_alerts_enabled', v)} />
          </FieldRow>
          <FieldRow label="Low-stock alerts" hint="Notify when a product runs low.">
            <Toggle checked={form.low_stock_alerts_enabled} onChange={(v) => set('low_stock_alerts_enabled', v)} />
          </FieldRow>
        </div>
      </SectionCard>

      <SectionCard title="Shipping & returns">
        <div className="divide-y divide-gray-100">
          <FieldRow label="Default shipping rate" hint="Fallback rate (GHS) when a listing has none.">
            <Input
              type="number"
              value={form.default_shipping_rate}
              onChange={(e) => set('default_shipping_rate', e.target.value)}
              className="w-28"
              placeholder="0.00"
            />
          </FieldRow>
          <FieldRow label="Return window" hint="Days a buyer can request a return.">
            <Input
              type="number"
              value={form.return_window_days}
              onChange={(e) => set('return_window_days', e.target.value)}
              className="w-28"
              placeholder="14"
            />
          </FieldRow>
          <FieldRow label="Auto-refund on approval" hint="Refund automatically when a return is approved.">
            <Toggle checked={form.auto_refund_enabled} onChange={(v) => set('auto_refund_enabled', v)} />
          </FieldRow>
        </div>
      </SectionCard>

      <SectionCard title="Tax">
        <div className="divide-y divide-gray-100">
          <FieldRow label="Charge VAT" hint="Apply VAT to this store's orders.">
            <Toggle checked={form.charge_vat} onChange={(v) => set('charge_vat', v)} />
          </FieldRow>
          <FieldRow label="VAT rate" hint="Percentage, e.g. 15.">
            <Input
              type="number"
              value={form.vat_rate}
              onChange={(e) => set('vat_rate', e.target.value)}
              className="w-28"
              placeholder="15"
              disabled={!form.charge_vat}
            />
          </FieldRow>
          <FieldRow label="Default tax category" hint="Applied to listings without an explicit category.">
            <Input
              value={form.default_tax_category}
              onChange={(e) => set('default_tax_category', e.target.value)}
              className="w-48"
              placeholder="standard"
            />
          </FieldRow>
          <FieldRow label="Levy tax ids" hint="For a special/custom category: comma-separated taxids.">
            <Input
              value={form.default_taxids}
              onChange={(e) => set('default_taxids', e.target.value.replace(/[^\d,\s]/g, ''))}
              className="w-48"
              placeholder="e.g. 3, 7"
            />
          </FieldRow>
        </div>
      </SectionCard>

      <SectionCard title="Plan lapse">
        <div className="divide-y divide-gray-100">
          <FieldRow label="Grace period" hint="Days after the plan expires before the store is closed.">
            <Input
              type="number"
              value={form.grace_period_days}
              onChange={(e) => set('grace_period_days', e.target.value)}
              className="w-28"
              placeholder="7"
            />
          </FieldRow>
        </div>
        <SaveRow
          dirty={dirty && taxidsValid}
          saving={update.isPending}
          onSave={() => setConfirm(true)}
          onReset={() => setForm(initial)}
          error={!taxidsValid ? 'Tax ids must be positive whole numbers.' : null}
        />
        {confirm && (
          <ReasonDialog
            open
            title="Save operations settings"
            description="Changes apply to the seller's store immediately."
            confirmLabel="Save"
            reasonRequired
            loading={update.isPending}
            onConfirm={save}
            onClose={() => setConfirm(false)}
          />
        )}
      </SectionCard>
    </div>
  );
};
